// contexts/ThemeContext.tsx
"use client"; // This Provider is a Client Component

import React, { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { Theme, getThemeByHost } from '@/lib/themes'; // Adjust path as needed

interface ThemeContextType {
    theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
    initialHost?: string | null; // Passed from server component
}

function hexToHSL(hex: string): string {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map((char) => char + char).join('');
    }
  
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
  
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
  
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h = h * 60;
    }
  
    s = s * 100;
    l = l * 100;
    return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
  }
  

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialHost }) => {
    // Always prioritize server-provided initialHost to prevent hydration mismatches
    const theme = useMemo(() => {
        // If we have initialHost from server, always use it
        if (initialHost) {
            return getThemeByHost(initialHost);
        }
        
        // Only fallback to client-side detection if no initialHost provided
        if (typeof window !== 'undefined') {
            return getThemeByHost(window.location.hostname);
        }
        
        // Final fallback to default theme
        return getThemeByHost(null);
    }, [initialHost]);

    useEffect(() => {
        // CSS variables disabled for now since we're using static colors
        // if (typeof window !== 'undefined') {
        //     const root = document.documentElement;
        //     root.style.setProperty('--primary', theme.colors.primary);
        //     root.style.setProperty('--secondary', theme.colors.secondary);
        //     root.style.setProperty('--accent', theme.colors.accent);
        // }
    }, [theme]);
      

    return (
        <ThemeContext.Provider value={{ theme }}>
          {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
